import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import { FlexBehavior, FlexJustify, Spinner } from 'react-magma-dom';
import { RouteComponentProps } from '@reach/router';
import { RelatedWrapperComponent } from '../../components/RelatedElement/RelatedWrapperComponent';
import { RelatedItem } from '../../components/RelatedElement';
import { ShortRelatedItem } from '../../components/RelatedElement/ShortRelatedItem';
import { Filter } from '../../components/Filter';
import { ContactUsError } from '../../components/ContactUsError';
import { ComingSoon } from '../../components/ComingSoon';
import { StyledCenteredSpinnerWrapper } from '../../components/Styled';
import { StyledHeading, StyledHeadingNote } from '../../components/Styled/StyledRelatedNav';
import { useQuery } from '../../hooks/useQuery';
import { planningApi } from '../../API/planning';
import { stagesApi } from '../../API/stages';
import { getStageColors } from '../../utils';
import { getHeadingMargin } from '../../utils/assessment';
import { computeStageFilterItems, computeSubcategoriesFilterItems, getAccessibleStages } from '../../utils/filter';
import { CategoryFilter, StageFilter } from '../../types/filter';
import {
  NOT_FOUND,
  PATHWAYS,
  PLANNING_CATEGORIES,
  RELATED_ITEM_WIDTH,
  SCOPE,
  SHORT_RELATED_ITEM_WIDTH,
  TEXT_TYPES,
} from '../../constants';
import { IAsset, IAssetCategory } from '../../constants/assets';

interface IPlanning extends RouteComponentProps {
  stage: string;
}

export const Planning = (props: IPlanning): JSX.Element => {
  const { stage, navigate } = props;

  const [items, setItems] = useState<IAssetCategory | undefined>();
  const [filteredAssets, setFilteredAssets] = useState<IAssetCategory['assets']>([]);
  const [filteredSubcategories, setFilteredSubcategories] = useState<IAssetCategory['subcategories']>([]);
  const [selectedFilter, setSelectedFilter] = useState<StageFilter | CategoryFilter>(StageFilter.all);
  const [accessibleStages, setAccessibleStages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isComingSoon, setIsComingSoon] = useState(false);
  const [error, setError] = useState(false);
  const filterItems = useMemo(
    () =>
      stage === SCOPE
        ? computeSubcategoriesFilterItems(items?.subcategories)
        : computeStageFilterItems(accessibleStages),
    [items, accessibleStages]
  );
  const defaultFilter = useQuery('filter');

  const { main: mainColor, gradient: gradientColor } = getStageColors(stage);

  useEffect(() => {
    if (!PLANNING_CATEGORIES.includes(stage)) {
      navigate && navigate(`/${NOT_FOUND}`);
    }

    (async () => {
      setItems(undefined);
      setFilteredAssets([]);
      setFilteredSubcategories([]);
      setIsLoading(true);

      try {
        const {
          data: { data },
        } = await planningApi.getPlanning(stage);
        setItems(data);
        setIsComingSoon(isEmpty(data));
      } catch (error) {
        setError(!!error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [stage]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      try {
        const {
          data: { data },
        } = await stagesApi.getStages();
        setAccessibleStages(getAccessibleStages(data));
      } catch (error) {
        setError(!!error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (items) {
      setFilteredSubcategories([]);
      setFilteredAssets([]);
      onChangeFilter((defaultFilter ?? StageFilter.all) as StageFilter);
    }
  }, [items]);

  const onChangeFilter = (filterState: StageFilter | CategoryFilter) => {
    if (![...Object.values(StageFilter), ...Object.values(CategoryFilter)].includes(filterState)) {
      filterState = StageFilter.all;
    }

    const { assets = [], subcategories = [] } = items || {};
    setSelectedFilter(filterState);

    const isAllSelected = filterState === StageFilter.all;
    stage === SCOPE
      ? setFilteredSubcategories(
          isAllSelected ? subcategories : subcategories?.filter(({ name }) => name.replace(/ .*/, '') === filterState)
        )
      : setFilteredAssets(isAllSelected ? assets : assets?.filter(({ stages }) => stages.includes(filterState)));
  };

  const renderShortRelatedItem = (item: IAssetCategory) => {
    if (item.assets) {
      return renderAssets(item.assets);
    }
  };

  const renderAssets = (assets: IAsset[]) => {
    return assets.map((asset, index) => (
      <ShortRelatedItem
        title={asset.title}
        type={asset.type}
        fileLink={asset.links.self}
        defaultColor={mainColor}
        gradient={gradientColor}
        key={index}
      />
    ));
  };

  const renderRelated = (assets: IAsset[]) => {
    return assets.map((asset, index) => <RelatedItem key={v4()} asset={asset} index={index} />);
  };

  const isEmpty = (category: IAssetCategory) => {
    return !category.subcategories && !category.assets;
  };

  if (error) {
    return <ContactUsError />;
  }

  if (isLoading) {
    return (
      <StyledCenteredSpinnerWrapper
        behavior={FlexBehavior.container}
        justify={FlexJustify.center}
        data-testid="spinner"
      >
        <Spinner color={mainColor} size={50} />
      </StyledCenteredSpinnerWrapper>
    );
  }

  if (isComingSoon) {
    return <ComingSoon />;
  }

  return (
    <RelatedWrapperComponent width={stage === SCOPE ? RELATED_ITEM_WIDTH : SHORT_RELATED_ITEM_WIDTH}>
      {[SCOPE, PATHWAYS, TEXT_TYPES].includes(stage) && (
        <Filter items={filterItems} selectedItem={selectedFilter} onChange={onChangeFilter} />
      )}
      {filteredAssets && (stage === SCOPE ? renderAssets(filteredAssets) : renderRelated(filteredAssets))}
      {filteredSubcategories &&
        filteredSubcategories.map((subcategory, index) => {
          return (
            <Fragment key={index}>
              <StyledHeading margin={getHeadingMargin(index)} size={24}>
                {subcategory.name}
                {isEmpty(subcategory) && <StyledHeadingNote>(Coming soon!)</StyledHeadingNote>}
              </StyledHeading>
              {renderShortRelatedItem(subcategory)}
            </Fragment>
          );
        })}
    </RelatedWrapperComponent>
  );
};
